import { useState } from "react";
import { Popover as AriaPopover, Dialog, DialogTrigger, type Key } from "react-aria-components";
import useMeasure from "react-use-measure";

import { InputHeader } from "@/components/ui/inputs/shared/InputHeader";

import type { DropdownProps } from "./Dropdown";
import { DropdownButton } from "./DropdownButton";
import { DropdownListBox } from "./DropdownListBox";

export const DropdownDesktop = <TKey extends Key = Key>(props: DropdownProps<TKey>) => {
  const { label, items, placeholder, value, selectionMode, variant, customTrigger, hideLabels, className } = props;

  const selectedItems = items.filter((item) =>
    selectionMode === "multiple" ? value.includes(item.id) : item.id === value,
  );

  const [triggerRef, { width }] = useMeasure();

  const [isOpen, setIsOpen] = useState(false);

  const onChange: DropdownProps<TKey>["onChange"] = (val: typeof value) => {
    if (selectionMode === "multiple") {
      props.onChange(val as TKey[]);
    } else {
      props.onChange(val as TKey);
      setIsOpen(false);
    }
  };

  const listboxProps = {
    ...props,
    onChange,
  } as DropdownProps<TKey>;

  return (
    <div className="group">
      <InputHeader
        label={label}
        hideLabels={hideLabels}
        className="!font-secondary text-label-5"
      />
      <DialogTrigger
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        {customTrigger ?? (
          <DropdownButton
            ref={triggerRef}
            variant={variant}
            className={className}
          >
            <span className={selectedItems.length > 0 ? "" : "text-text-default-tertiary"}>
              {selectedItems.length > 0 ? selectedItems.map((item) => item.label).join(", ") : placeholder}
            </span>
          </DropdownButton>
        )}

        <AriaPopover
          className="my-4 outline-none"
          style={{ width: !customTrigger ? width : undefined }}
          offset={0}
        >
          <Dialog className="outline-none">
            <div
              className="overflow-hidden rounded-input-rounding border border-elevation-outline-1 bg-elevation-surface-1 shadow-5 outline-none"
              key={props.search}
            >
              <DropdownListBox {...listboxProps} />
            </div>
          </Dialog>
        </AriaPopover>
      </DialogTrigger>
    </div>
  );
};
